/**
 * 招待一覧テーブル
 */

import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { type BadgeTone, StatusBadge } from "components/ui/StatusBadge";
import type { Invitation } from "hooks/useInvitationsApi";
import type { FC } from "react";
import { useState } from "react";

type InvitationTableProps = {
  invitations: Invitation[];
  onCancel: (invitationId: string) => Promise<void>;
  isCancelling: boolean;
};

const statusLabels: Record<string, { label: string; tone: BadgeTone }> = {
  pending: { label: "保留中", tone: "soft-warning" },
  accepted: { label: "承認済み", tone: "soft-success" },
  expired: { label: "期限切れ", tone: "soft-error" },
  cancelled: { label: "キャンセル済み", tone: "soft-neutral" },
};

const roleLabels: Record<string, string> = {
  owner: "オーナー",
  admin: "管理者",
  member: "メンバー",
};

export const InvitationTable: FC<InvitationTableProps> = ({
  invitations,
  onCancel,
  isCancelling,
}) => {
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCancel = async (invitationId: string) => {
    if (!window.confirm("この招待をキャンセルしますか？")) return;
    setCancellingId(invitationId);
    try {
      await onCancel(invitationId);
    } finally {
      setCancellingId(null);
    }
  };

  const handleCopyUrl = async (invitation: Invitation) => {
    await navigator.clipboard.writeText(invitation.invitationUrl);
    setCopiedId(invitation.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (invitations.length === 0) {
    return (
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <h3 className="card-title text-lg">招待一覧</h3>
          <p className="text-base-content/60">まだ招待がありません</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow-sm">
      <div className="card-body">
        <h3 className="card-title text-lg">招待一覧</h3>
        <div className="overflow-x-auto">
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>メールアドレス</th>
                <th>ロール</th>
                <th>ステータス</th>
                <th>有効期限</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {invitations.map(invitation => {
                const status = statusLabels[invitation.status] || {
                  label: invitation.status,
                  tone: "soft-neutral" as BadgeTone,
                };
                const expiresAt = new Date(invitation.expiresAt);
                const isExpired = expiresAt < new Date();
                const isPending = invitation.status === "pending";

                return (
                  <tr key={invitation.id}>
                    <td className="font-mono text-sm">{invitation.email}</td>
                    <td>{roleLabels[invitation.role] || invitation.role}</td>
                    <td>
                      <StatusBadge tone={status.tone}>
                        {status.label}
                      </StatusBadge>
                    </td>
                    <td className={isExpired ? "text-error" : ""}>
                      {expiresAt.toLocaleDateString("ja-JP")}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        {isPending && !isExpired && (
                          <>
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => handleCopyUrl(invitation)}
                            >
                              {copiedId === invitation.id
                                ? "コピー済み"
                                : "URLコピー"}
                            </Button>
                            <Button
                              size="xs"
                              variant="destructive"
                              isOutline
                              onClick={() => handleCancel(invitation.id)}
                              disabled={
                                isCancelling && cancellingId === invitation.id
                              }
                            >
                              {isCancelling &&
                              cancellingId === invitation.id ? (
                                <Loading size="xs" />
                              ) : (
                                "キャンセル"
                              )}
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
